import i18n, { createInstance, i18n as I18nInstance, Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getFrontendRuntimeContext } from './frontendRuntimeContext';

const i18nOptions = (localeConfig: any) => ({
  debug: !!localeConfig.debug,
  load: 'languageOnly' as const,
  lng: localeConfig.code || 'en',
  preload: ['en'],
  initImmediate: true,
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false
  },
  resources: localeConfig.resources,
  react: {
    useSuspense: false
  }
});

export function setupI18n(localeConfig: any) {
  // for all options read: https://www.i18next.com/overview/configuration-options
  return i18n.use(initReactI18next).init(i18nOptions(localeConfig));
}

const frozenResources = new WeakSet<object>();

function freezeResource(value: any): void {
  if (!value || typeof value !== 'object' || frozenResources.has(value)) {
    return;
  }
  for (const child of Object.values(value)) {
    freezeResource(child);
  }
  Object.freeze(value);
  frozenResources.add(value);
}

export async function createRequestI18n(localeConfig: any): Promise<I18nInstance> {
  // Locale-service dictionaries are immutable. Only language/namespace tables belong
  // to each request; copying every translated item on each render is prohibitively large.
  const resources: Resource = {};
  for (const [language, namespaces] of Object.entries(localeConfig.resources as Resource)) {
    freezeResource(namespaces);
    resources[language] = { ...namespaces };
  }
  const requestI18n = createInstance();
  await requestI18n.init({
    ...i18nOptions(localeConfig),
    resources
  });

  // i18next's public mutators and addResources delegate to these store methods.
  // Copy an existing namespace before nested writes; new bundles and removals
  // already operate on the request-owned tables. Never change the global instance.
  const store = requestI18n.store as typeof requestI18n.store & Pick<I18nInstance, 'addResource' | 'addResourceBundle'>;
  const ownNamespace = (language: string, namespace: string) => {
    // i18next also accepts a dotted resource path in the language argument.
    const [lng, ns] = language.includes('.') ? language.split('.') : [language, namespace];
    const bundle = resources[lng]?.[ns];
    if (bundle && typeof bundle === 'object' && frozenResources.has(bundle)) {
      resources[lng][ns] = structuredClone(bundle);
    }
  };
  const addResource = store.addResource.bind(store);
  store.addResource = (...args) => {
    ownNamespace(args[0], args[1]);
    return addResource(...args);
  };
  const addResourceBundle = store.addResourceBundle.bind(store);
  store.addResourceBundle = (...args) => {
    ownNamespace(args[0], args[1]);
    return addResourceBundle(...args);
  };
  return requestI18n;
}

export function getI18n(): I18nInstance {
  return getFrontendRuntimeContext()?.i18n || i18n;
}
